import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import ContractCard from '../components/ContractCard';
import ListingCard from '../components/ListingCard';
import BuyerAgreementCard from '../components/BuyerAgreementCard';
import ReferralAgreementCard from '../components/ReferralAgreementCard';
import LoadingSpinner from '../components/LoadingSpinner';
import CreateFormModal from '../components/CreateFormModal';
import { wizardSpecForFormType } from '../utils/formTypeRegistry';
import {
  buildPortalCreateSubmit,
  createNewClickIntent,
  resolveDashboardFormClose,
  returnToCatalogPicker,
  startDashboardForm,
} from '../utils/dashboardFormLaunch';
import useViewerSubscription from '../hooks/useViewerSubscription';
import ChatBox from '../components/ChatBox';
import FirstRunChecklist from '../components/FirstRunChecklist';
import GuestHomeEmptyState from '../components/GuestHomeEmptyState';
import GuestUpgradeModal from '../components/GuestUpgradeModal';
import InboundSyncImportBanner from '../components/InboundSyncImportBanner';
import './ContractsList.css';
import Logo from '../components/Logo';
import HamburgerMenuButton from '../components/HamburgerMenuButton';
import useDismissOnOutsideClick from '../hooks/useDismissOnOutsideClick';
import { DeveloperMenuItem } from '../components/DeveloperNavigation';
import { billingAuthHeaders } from '../utils/billingApi';
import { getBackendBaseUrl } from '../utils/apiUrls';
import { getContractApiUrl } from '../utils/apiUrls';
import { portalLoginAuthorizationParams } from '../utils/authSession';
import { getListingsApiUrl } from '../utils/apiUrls';
import { getReferralsApiUrl } from '../utils/apiUrls';
import { getTmsApiUrl } from '../utils/apiUrls';
import {
  matchesSearch,
  PORTAL_SEARCH_PLACEHOLDER,
} from '../utils/portalTransactionSearch';
import { shouldShowFirstRunChecklist } from '../utils/firstRunChecklist';
import { fetchGuestUpgradeOffer } from '../utils/guestUpgradeOffer';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

const LISTINGS_URL = getListingsApiUrl();

const BUYER_AGREEMENTS_URL = getBackendBaseUrl();

const REFERRALS_URL = getReferralsApiUrl();

const TMS_URL = getTmsApiUrl();

const REWARDS_SESSION_EXPIRED_MESSAGE = 'Your session expired. Log out and back in, then open Rewards again.';


const ContractsList = () => {
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [listings, setListings] = useState([]);
  const [buyerAgreements, setBuyerAgreements] = useState([]);
  const [referralAgreements, setReferralAgreements] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalStartOnOther, setCreateModalStartOnOther] = useState(false);
  const [dashboardFormType, setDashboardFormType] = useState(null);
  const [dashboardFormCard, setDashboardFormCard] = useState(null);
  const dashboardCreatedRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const userMenuRef = useRef(null);
  const dismissUserMenu = useCallback(() => {
    setShowDropdown(false);
  }, []);
  useDismissOnOutsideClick(userMenuRef, showDropdown, dismissUserMenu);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [showContracts, setShowContracts] = useState(true);
  const [showListings, setShowListings] = useState(true);
  const [showBuyerAgreements, setShowBuyerAgreements] = useState(true);
  const [showReferrals, setShowReferrals] = useState(false);
  const referralsDefaultApplied = useRef(false);
  const inboundSyncPrevStatusRef = useRef(null);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [pendingEmailCount, setPendingEmailCount] = useState(0);
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsError, setRewardsError] = useState('');
  const [referralLink, setReferralLink] = useState(null);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copiedReferralLink, setCopiedReferralLink] = useState(false);
  const [tmsConfig, setTmsConfig] = useState(null);
  const [inboundSyncStatus, setInboundSyncStatus] = useState(null);
  const [showGuestUpgradeModal, setShowGuestUpgradeModal] = useState(false);
  const [guestUpgradeOffer, setGuestUpgradeOffer] = useState({
    loading: false,
    freeTrialDeals: 1,
    planNames: [],
  });
  const { isAuthenticated, loginWithRedirect, user, logout, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const {
    accessReady,
    isGuest: accessIsGuest,
    canGenerateForms,
    formGenerationPlanBlocked,
    currentPlanName,
    upgradePlanName,
    profile: viewerProfile,
  } = useViewerSubscription();

  const handleLogin = () => {
    loginWithRedirect({
      appState: { returnTo: location.pathname },
      authorizationParams: portalLoginAuthorizationParams(),
    });
  };
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      checkUserProfile();
    }
  }, [isAuthenticated, user]);

  // Fetch pending email count for notification badge
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      axios.get(`${BACKEND_URL}/pending-emails/count?email=${encodeURIComponent(user.email)}`)
        .then(res => setPendingEmailCount(res.data?.count || 0))
        .catch(() => { }); // Silently ignore
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && userProfile?.profile_completed) {
      const loadData = async () => {
        setLoading(true);
        // Parallel fetch for speed
        await Promise.all([fetchContracts(), fetchListings(), fetchTasks(), fetchBuyerAgreements(), fetchReferralAgreements()]);
        setLoading(false);
      };
      loadData();
    }
  }, [isAuthenticated, userProfile]);

  const fetchTmsConfig = async () => {
    // Guests may connect SkySlope and auto-start inbound (IS-2 banner); do not skip.
    if (!user?.email) {
      return;
    }
    try {
      const res = await axios.get(`${TMS_URL}/config?email=${encodeURIComponent(user.email)}`);
      setTmsConfig(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setTmsConfig(null);
      }
    }
  };

  const fetchInboundSyncStatus = async () => {
    if (!user?.email || !tmsConfig?.tms_type) {
      return null;
    }
    try {
      const res = await axios.get(`${TMS_URL}/inbound-sync/status?email=${encodeURIComponent(user.email)}`);
      setInboundSyncStatus(res.data);
      return res.data;
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error fetching inbound sync status:', err);
      }
      return null;
    }
  };

  useEffect(() => {
    if (userProfile?.profile_completed) {
      fetchTmsConfig();
    }
  }, [userProfile?.profile_completed, user?.email]);

  useEffect(() => {
    if (tmsConfig?.tms_type === 'skyslope') {
      fetchInboundSyncStatus();
    }
  }, [tmsConfig?.tms_type, user?.email]);

  useEffect(() => {
    const status = inboundSyncStatus?.status;
    if (!status || !['running', 'pending'].includes(status)) {
      return undefined;
    }
    const timer = setInterval(() => {
      fetchInboundSyncStatus();
    }, 5000);
    return () => clearInterval(timer);
  }, [inboundSyncStatus?.status, tmsConfig?.tms_type, user?.email]);

  useEffect(() => {
    const status = (inboundSyncStatus?.status || '').toLowerCase();
    const prevStatus = inboundSyncPrevStatusRef.current;
    inboundSyncPrevStatusRef.current = status;

    const wasInProgress = prevStatus && ['running', 'pending'].includes(prevStatus);
    const isComplete = status === 'completed' || status === 'complete';
    if (
      wasInProgress
      && isComplete
      && isAuthenticated
      && userProfile?.profile_completed
    ) {
      Promise.all([
        fetchContracts(),
        fetchListings(),
        fetchBuyerAgreements(),
        fetchReferralAgreements(),
      ]).catch((err) => {
        console.error('Error refreshing transactions after import:', err);
      });
    }
  }, [inboundSyncStatus?.status, isAuthenticated, userProfile?.profile_completed]);

  useEffect(() => {
    if (referralsDefaultApplied.current || loading) return;
    const referralsOnlyUser =
      referralAgreements.length > 0 &&
      contracts.length === 0 &&
      listings.length === 0 &&
      buyerAgreements.length === 0;
    if (referralsOnlyUser) {
      setShowReferrals(true);
      referralsDefaultApplied.current = true;
    }
  }, [loading, contracts, listings, buyerAgreements, referralAgreements]);

  const checkUserProfile = async () => {
    if (!user?.email) return;

    try {
      setProfileLoading(true);
      const response = await axios.get(`${BACKEND_URL}/user/profile?email=${encodeURIComponent(user.email)}`);

      setUserProfile({
        ...response.data,
        exists: true,
        profile_completed: response.data.profile_completed
      });

    } catch (err) {
      console.error('Error checking user profile:', err);

      if (err.response?.status === 404) {
        setUserProfile({ exists: false, profile_completed: false });
        // Redirect to profile if missing
        navigate('/profile?onboarding=1');
      }
      else if (err.code === 'ERR_NETWORK' || err.response?.status >= 500) {
        setUserProfile({ exists: false, profile_completed: false });
      } else {
        setError('Failed to check user profile. Please try again.');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(dateString);
    } catch {
      return null;
    }
  };

  const isContractFullyExecuted = (contract) => Boolean(contract?.fully_executed_date);

  const contractClosingDate = (contract) => (
    contract?.close_of_escrow_due_date ? parseLocalDate(contract.close_of_escrow_due_date) : null
  );

  const isCancelledContract = (contract) => contract?.deal_status === 'cancelled';

  /** Closed = cancelled, explicit deal_status, or KeyFlo has a closing document — not COE date alone. */
  const isClosedContract = (contract) => {
    if (isCancelledContract(contract)) return true;
    if (contract?.deal_status === 'closed') return true;
    return Boolean(contract?.has_closing_documents);
  };

  /** Active contracts are everything not closed (including past COE without closing docs). */
  const isActiveContract = (contract) => !isClosedContract(contract);

  const contractSortDate = (contract, forClosedView = false) => {
    if (isCancelledContract(contract)) {
      const cancelled = contract.cancelled_at ? new Date(contract.cancelled_at) : null;
      if (cancelled && !Number.isNaN(cancelled.getTime())) return cancelled;
    }
    if (!isContractFullyExecuted(contract)) {
      const uploaded = contract.upload_date ? new Date(contract.upload_date) : null;
      if (uploaded && !Number.isNaN(uploaded.getTime())) return uploaded;
    }
    return contractClosingDate(contract) || (forClosedView ? new Date('1900-01-01') : new Date('9999-12-31'));
  };

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/contracts`, {
        params: { user_email: user.email }
      });
      const DEMO_ADDR = '4521 willow creek dr';
      const isDemoContract = (c) => (c.property_address || '').toLowerCase().includes(DEMO_ADDR);
      const sortedContracts = response.data.contracts.sort((a, b) => {
        if (isDemoContract(a) && !isDemoContract(b)) return -1;
        if (isDemoContract(b) && !isDemoContract(a)) return 1;
        const dateA = contractSortDate(a);
        const dateB = contractSortDate(b);
        if (!dateA || !dateB) return 0;
        return dateA - dateB;
      });

      setContracts(sortedContracts);
      setFilteredContracts(sortedContracts);
      setError(null);
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError('Failed to load contracts.');
    }
  };

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${LISTINGS_URL}/`, {
        params: { user_email: user.email },
      });
      setListings(response.data || []);
    } catch (err) {
      console.error('Error fetching listings:', err);
      // Non-fatal — listings section will just be empty
    }
  };

  const fetchBuyerAgreements = async () => {
    try {
      const response = await axios.get(`${BUYER_AGREEMENTS_URL}/buyer-agreements/`, {
        params: { user_email: user.email },
      });
      setBuyerAgreements(response.data || []);
    } catch (err) {
      console.error('Error fetching buyer agreements:', err);
      // Non-fatal
    }
  };

  const fetchReferralAgreements = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/referral-agreements`, {
        params: { user_email: user.email },
      });
      setReferralAgreements(response.data || []);
    } catch (err) {
      console.error('Error fetching referral agreements:', err);
    }
  };

  const fetchTasks = async () => {
    try {
      // Fetch urgent tasks: to-do, in-progress. 
      const response = await fetch(`${BACKEND_URL}/tasks?status=to-do,in-progress&user_email=${encodeURIComponent(user.email)}`);
      if (!response.ok) throw new Error('Failed to fetch tasks');

      const data = await response.json();
      const allTasks = data.tasks || [];

      // Sort by due date (earliest first)
      allTasks.sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;

        const dateA = parseLocalDate(a.due_date);
        const dateB = parseLocalDate(b.due_date);

        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateA - dateB;
      });

      setTasks(allTasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      // Suppress task error to avoid blocking contract view, just show empty tasks or console error
    }
  };

  const filterAndSearchContracts = () => {
    let filtered = [...contracts];

    if (showActive && !showClosed) {
      // Show Active Only
      filtered = filtered.filter(contract => isActiveContract(contract));
    } else if (!showActive && showClosed) {
      // Show Closed Only
      filtered = filtered.filter(contract => isClosedContract(contract));
    } else if (!showActive && !showClosed) {
      // Show None
      filtered = [];
    }
    // If both true, show all (no filter applied)

    if (searchTerm.trim()) {
      filtered = filtered.filter(contract =>
        matchesSearch('contract', contract, searchTerm)
      );
    }

    const DEMO_ADDR_LOWER = '4521 willow creek dr';
    const isDemo = (c) => (c.property_address || '').toLowerCase().includes(DEMO_ADDR_LOWER);
    if (!showActive && showClosed) {
      filtered.sort((a, b) => {
        if (isDemo(a) && !isDemo(b)) return -1;
        if (isDemo(b) && !isDemo(a)) return 1;
        const dateA = contractSortDate(a, true);
        const dateB = contractSortDate(b, true);
        return dateB - dateA;
      });
    } else {
      filtered.sort((a, b) => {
        if (isDemo(a) && !isDemo(b)) return -1;
        if (isDemo(b) && !isDemo(a)) return 1;
        const dateA = contractSortDate(a);
        const dateB = contractSortDate(b);
        return dateA - dateB;
      });
    }

    setFilteredContracts(filtered);
  };

  useEffect(() => {
    filterAndSearchContracts();
  }, [contracts, searchTerm, showActive, showClosed]);

  const handleContractClick = (contractId) => navigate(`/contract/${contractId}`);
  const handleLogout = () => { sessionStorage.removeItem('keyflo_login_event_fired'); logout({ logoutParams: { returnTo: window.location.origin } }); };
  const handleDashboardWizardClose = (result) => {
    const spec = wizardSpecForFormType(dashboardFormType, {
      createsEntity: dashboardFormCard?.createsEntity,
      catalog: dashboardFormCard
        ? [{ group: 'picked', forms: [dashboardFormCard] }]
        : undefined,
    });
    const decision = resolveDashboardFormClose({
      spec,
      result,
      storedResult: dashboardCreatedRef.current,
      isAuthenticated,
    });
    dashboardCreatedRef.current = null;
    setDashboardFormType(null);
    setDashboardFormCard(null);
    if (decision.action === 'navigate') {
      navigate(decision.path);
      return;
    }
    if (decision.action !== 'refresh') return;
    if (decision.refresh === 'listings') fetchListings();
    else if (decision.refresh === 'buyerAgreements') fetchBuyerAgreements();
    else fetchContracts();
  };
  const handleProfileClick = () => { navigate('/profile'); setShowDropdown(false); };

  const handleDropdownToggle = () => setShowDropdown(!showDropdown);

  const isTransactionGuest = accessReady
    ? accessIsGuest
    : userProfile?.is_transaction_guest === true;

  const inboundSyncInProgress = ['running', 'pending'].includes(
    (inboundSyncStatus?.status || '').toLowerCase(),
  );

  const guestImportPropertyAddress = (() => {
    if (!isTransactionGuest) return null;
    const demoNeedle = '4521 willow creek dr';
    const pecanNeedle = '456 pecan creek';
    const isDemoAddr = (addr) => {
      const lower = (addr || '').toLowerCase();
      return lower.includes(demoNeedle) || lower.includes(pecanNeedle);
    };
    for (const c of contracts || []) {
      const addr = (c.property_address || '').trim();
      if (addr && !isDemoAddr(addr)) return addr;
    }
    for (const l of listings || []) {
      const addr = (l.street_address || l.property_address || '').trim();
      if (addr && !isDemoAddr(addr)) return addr;
    }
    return null;
  })();

  useEffect(() => {
    if (!isAuthenticated || !isTransactionGuest) {
      return undefined;
    }
    let cancelled = false;
    setGuestUpgradeOffer((prev) => ({ ...prev, loading: true }));
    fetchGuestUpgradeOffer()
      .then((offer) => {
        if (!cancelled) {
          setGuestUpgradeOffer({
            loading: false,
            freeTrialDeals: offer.freeTrialDeals,
            planNames: offer.planNames,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGuestUpgradeOffer((prev) => ({ ...prev, loading: false }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isTransactionGuest]);

  const handleGuestUpgradePrompt = () => {
    setShowGuestUpgradeModal(true);
  };

  const handleCreateNewClick = () => {
    const intent = createNewClickIntent(
      isTransactionGuest,
      (accessReady || viewerProfile) ? canGenerateForms : true,
      (accessReady || viewerProfile) ? formGenerationPlanBlocked : false,
    );
    if (intent === 'upgrade') {
      handleGuestUpgradePrompt();
      return;
    }
    if (intent === 'plans') {
      navigate('/profile?tab=subscription');
      return;
    }
    setCreateModalStartOnOther(false);
    setShowCreateModal(true);
  };

  const handleGuestUpgrade = () => {
    setShowGuestUpgradeModal(false);
    navigate('/profile?upgrade=1');
  };

  const rewardsAuthHeaders = async () => (
    billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims })
  );

  const rewardsErrorMessage = (err, fallback) => {
    if (err.response?.status === 401) return REWARDS_SESSION_EXPIRED_MESSAGE;
    if (
      err.error === 'invalid_grant' ||
      err.error === 'login_required' ||
      err.message?.toLowerCase().includes('refresh token') ||
      err.message?.toLowerCase().includes('sign in required')
    ) {
      return REWARDS_SESSION_EXPIRED_MESSAGE;
    }
    return err.response?.data?.error || fallback;
  };

  const fetchReferralLink = async () => {
    if (!user?.email) return;
    setRewardsLoading(true);
    setRewardsError('');
    try {
      const headers = await rewardsAuthHeaders();
      const response = await axios.post(
        `${REFERRALS_URL}/links`,
        { channel: 'direct_link', email: user.email },
        { params: { email: user.email }, headers },
      );
      setReferralLink(response.data);
    } catch (err) {
      console.error('Error creating referral link:', err);
      setRewardsError(rewardsErrorMessage(err, 'Unable to load referral rewards.'));
    } finally {
      setRewardsLoading(false);
    }
  };

  const handleRewardsOpen = () => {
    setShowDropdown(false);
    setShowRewardsModal(true);
    setInviteResult(null);
    setRewardsError('');
    if (!referralLink) fetchReferralLink();
  };

  const handleCopyReferralLink = async () => {
    if (!referralLink?.referral_url) return;
    try {
      await navigator.clipboard.writeText(referralLink.referral_url);
      setCopiedReferralLink(true);
      setTimeout(() => setCopiedReferralLink(false), 1800);
    } catch {
      setRewardsError('Copy failed. Select the link and copy it manually.');
    }
  };

  const parseInviteEmails = () => (
    inviteEmails
      .split(/[\s,;]+/)
      .map(email => email.trim().toLowerCase())
      .filter(Boolean)
  );

  const handleSendInvites = async () => {
    const recipients = Array.from(new Set(parseInviteEmails()));
    setInviteResult(null);
    setRewardsError('');
    if (recipients.length === 0) {
      setRewardsError('Add at least one email.');
      return;
    }
    if (recipients.length > 10) {
      setRewardsError('Send up to 10 emails at a time.');
      return;
    }

    setInviteSubmitting(true);
    try {
      const headers = await rewardsAuthHeaders();
      const response = await axios.post(
        `${REFERRALS_URL}/invites`,
        { recipients, email: user.email },
        { params: { email: user.email }, headers },
      );
      setInviteResult(response.data);
      if ((response.data?.sent || 0) > 0) {
        setInviteEmails('');
      }
    } catch (err) {
      console.error('Error sending referral invites:', err);
      setRewardsError(rewardsErrorMessage(err, 'Unable to send referral invites.'));
    } finally {
      setInviteSubmitting(false);
    }
  };

  if (!isAuthenticated) return (
    <div className="auth-container">
      <div className="auth-content">
        <Logo size={72} />
        <p>Please log in to view your contracts</p>
        <button className="login-button" onClick={handleLogin}>Log In</button>
      </div>
    </div>
  );

  if (profileLoading) return <LoadingSpinner />;

  if (userProfile && (!userProfile.profile_completed || !userProfile.exists)) {
    return (
      <div className="profile-required-container">
        <div className="profile-required-content">
          <h1>Complete Your Profile</h1>
          <p>Welcome to the Real Estate Contract Manager!</p>
          <button className="complete-profile-button" onClick={() => navigate('/profile')}>Complete Profile</button>
        </div>
      </div>
    );
  }

  // Helper to format due date for task card
  const getTaskDueText = (task) => {
    if (!task.due_date) return 'No Date';
    const due = parseLocalDate(task.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); due.setHours(0, 0, 0, 0);

    if (due.getTime() === today.getTime()) return 'Due Today';
    if (due < today) return 'Overdue';
    return `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const getTaskBorderColor = (task) => {
    if (task.status === 'completed') return 'var(--accent-green)';
    if (!task.due_date) return '#e0e0e0';

    const due = parseLocalDate(task.due_date);
    const today = new Date(); today.setHours(0, 0, 0, 0); due.setHours(0, 0, 0, 0);

    if (due < today) return '#e74c3c'; // Red for Overdue
    if (due.getTime() === today.getTime()) return '#D4A574'; // Gold/Orange for Today
    return '#3498db'; // Blue for Upcoming
  };

  // Get visible tasks (all or top 2)
  const visibleTasks = showAllTasks ? tasks : tasks.slice(0, 2);
  const rewardsOfferValue = referralLink?.offered_free_transactions;
  const rewardsOfferDisplay = rewardsLoading ? '…' : (rewardsOfferValue ?? '—');
  const homepageTransactionCount =
    contracts.length + listings.length + buyerAgreements.length + referralAgreements.length;
  const showFirstRunChecklist = !loading && shouldShowFirstRunChecklist({
    isTransactionGuest,
    profileCompleted: userProfile?.profile_completed === true,
    transactionCount: homepageTransactionCount,
  });

  return (
    <div className="contracts-list-container">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <Logo size={40} />
          </div>
          <div className="user-nav">
            <span>Welcome, {userProfile?.name ? userProfile.name.split(' ')[0] : (user?.given_name || user?.nickname || user?.email?.split('@')[0])}</span>
            <div className="user-menu" ref={userMenuRef}>
              <HamburgerMenuButton onClick={handleDropdownToggle} />
              {showDropdown && (
                <div className="dropdown-menu">
                  <button className="menu-item" onClick={handleProfileClick}>Profile</button>
                  <button className="menu-item" onClick={() => { setShowDropdown(false); navigate('/settings'); }}>
                    Settings
                    {pendingEmailCount > 0 && (
                      <span style={{
                        marginLeft: '8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        backgroundColor: '#D4A574',
                        color: '#ffffff',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        padding: '0 5px',
                      }}>{pendingEmailCount}</span>
                    )}
                  </button>
                  {user?.email?.endsWith('@keyflo.ai') && (
                    <DeveloperMenuItem
                      userEmail={user?.email}
                      onNavigate={(path) => {
                        setShowDropdown(false);
                        navigate(path);
                      }}
                    />
                  )}
                  <div className="divider"></div>
                  <button className="menu-item" style={{ color: '#e74c3c' }} onClick={() => { setShowDropdown(false); handleLogout(); }}>Log Out</button>
                </div>
              )}
            </div>
            {userProfile?.is_transaction_guest === true ? (
              /* #208: transaction guests see the Full Access trial CTA
                 in the slot where subscribers / trial owners see Rewards */
              <button className="rewards-header-button" onClick={() => navigate('/profile?upgrade=1')}>Get Full Access</button>
            ) : (
              <button className="rewards-header-button" onClick={handleRewardsOpen}>Rewards</button>
            )}
          </div>
        </div>
      </header>

      {showRewardsModal && (
        <div className="rewards-modal-backdrop" onClick={() => setShowRewardsModal(false)}>
          <div className="rewards-modal" onClick={(event) => event.stopPropagation()}>
            <div className="rewards-modal-header">
              <h2>KeyFlo Referral Offer</h2>
              <button className="rewards-close-button" onClick={() => setShowRewardsModal(false)} aria-label="Close rewards modal">
                <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false">
                  <path d="M1.5 1.5l9 9m0-9l-9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
                </svg>
              </button>
            </div>

            <div className="rewards-offer-panel">
              <p className="rewards-offer-lead">
                Earn a free KeyFlo transaction for every new, paid user you refer to KeyFlo.
              </p>
              <p className="rewards-offer-detail">
                Invite them to KeyFlo and they get{' '}
                <strong className="rewards-offer-pill">{rewardsOfferDisplay} free transactions</strong>{' '}
                just for signing up!
              </p>
            </div>

            <section className="rewards-section">
              <label htmlFor="referral-link">Referral link</label>
              <div className="rewards-copy-row">
                <input
                  id="referral-link"
                  type="text"
                  value={referralLink?.referral_url || (rewardsLoading ? 'Loading...' : '')}
                  readOnly
                />
                <button onClick={handleCopyReferralLink} disabled={!referralLink?.referral_url}>
                  {copiedReferralLink ? 'Copied' : 'Copy'}
                </button>
              </div>
            </section>

            <section className="rewards-section">
              <label htmlFor="referral-invites">Email invites</label>
              <textarea
                id="referral-invites"
                rows={4}
                value={inviteEmails}
                onChange={(event) => setInviteEmails(event.target.value)}
                placeholder="email@brokerage.com, someones.email@gmail.com"
              />
              <div className="rewards-actions-row">
                <span>{parseInviteEmails().length}/10</span>
                <div className="rewards-message-slot" aria-live="polite">
                  {(rewardsError || inviteResult) && (
                    <div className={`rewards-message ${rewardsError || inviteResult?.failed > 0 ? 'error' : 'success'}`}>
                      {rewardsError || (inviteResult.failed > 0 ? `${inviteResult.failed} failed to send` : 'Invites sent!')}
                    </div>
                  )}
                </div>
                <button onClick={handleSendInvites} disabled={inviteSubmitting || rewardsLoading}>
                  {inviteSubmitting ? 'Sending...' : 'Send Invites'}
                </button>
              </div>
            </section>
          </div>
        </div>
      )}

      <main className="main-content">
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => { fetchContracts(); fetchTasks(); }} className="retry-button">Retry</button>
          </div>
        )}

        <InboundSyncImportBanner
          visible={Boolean(tmsConfig?.tms_type === 'skyslope' && inboundSyncInProgress)}
          isGuest={isTransactionGuest}
          propertyAddress={guestImportPropertyAddress}
        />

        {/* My Tasks Section - HIDDEN */}
        {false && tasks.length > 0 && (
          <div className="tasks-section">
            <h2>My Tasks</h2>
            <div className="tasks-grid">
              {visibleTasks.map(task => {
                const contract = contracts.find(c => c.id === task.contract_id);
                const address = contract ? contract.property_address : `Contract #${task.contract_id}`;
                const borderColor = getTaskBorderColor(task);
                const dueText = getTaskDueText(task);
                const dueColor = borderColor === '#e0e0e0' ? 'var(--text-secondary)' : borderColor;

                return (
                  <div key={task.id} className="task-card" style={{ border: `2px solid ${borderColor}` }} onClick={() => handleContractClick(task.contract_id)}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>{address}</div>
                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>{task.task_name}</div>
                    <div style={{ fontSize: '0.8rem', color: dueColor, fontWeight: 500 }}>{dueText}</div>
                  </div>
                );
              })}
              <div className="task-card-action" onClick={() => setShowAllTasks(!showAllTasks)}>
                {showAllTasks ? '- View Less' : `+ View All ${tasks.length} Tasks`}
              </div>
            </div>
          </div>
        )}

        {!showFirstRunChecklist && (
        <>
        {/* KeyFlo AI Chat Box */}
        <ChatBox
          userEmail={user?.email}
          userName={userProfile?.name || user?.name || user?.nickname}
          getAccessTokenSilently={getAccessTokenSilently}
          getIdTokenClaims={getIdTokenClaims}
        />

        {/* Page Header: Title & Stats */}
        <div className="page-header">
          <h2>My Transactions</h2>
          <span className="stats-text">
            {contracts.length + listings.length + buyerAgreements.length} files
          </span>
        </div>

        {/* Controls Bar */}
        <div className="controls-bar">
          <div className="controls-bar-top">
            <div className="search-group">
              <input
                type="text"
                placeholder={PORTAL_SEARCH_PLACEHOLDER}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="btn-primary" onClick={handleCreateNewClick}>
              + Create Form
            </button>
          </div>
          <div className="controls-bar-filters">
            <button
              className={`filter-pill ${showActive ? 'active' : ''}`}
              onClick={() => setShowActive(!showActive)}
            >
              Active Files
            </button>
            <button
              className={`filter-pill ${showClosed ? 'active' : ''}`}
              onClick={() => setShowClosed(!showClosed)}
            >
              Closed Files
            </button>
            <button
              className={`filter-pill ${showContracts ? 'active' : ''}`}
              onClick={() => setShowContracts(!showContracts)}
            >
              Contracts
            </button>
            <button
              className={`filter-pill ${showListings ? 'active' : ''}`}
              onClick={() => setShowListings(!showListings)}
            >
              Listings
            </button>
            <button
              className={`filter-pill ${showBuyerAgreements ? 'active' : ''}`}
              onClick={() => setShowBuyerAgreements(!showBuyerAgreements)}
            >
              Buyer Agreements
            </button>
            <button
              className={`filter-pill ${showReferrals ? 'active' : ''}`}
              onClick={() => setShowReferrals(!showReferrals)}
            >
              Referrals
            </button>
          </div>
        </div>
        </>
        )}

        {/* Unified files grid — listings and contracts merged, sorted by date */}
        {(() => {
          if (loading) return <div className="contract-grid"><LoadingSpinner /></div>;

          // Apply type filters
          const visibleContracts = showContracts ? filteredContracts : [];
          let filteredListings = showListings ? [...listings] : [];
          if (showActive && !showClosed) {
            filteredListings = filteredListings.filter(l => l.status === 'active' || l.status === 'pending');
          } else if (!showActive && showClosed) {
            filteredListings = filteredListings.filter(l => l.status === 'sold' || l.status === 'withdrawn' || l.status === 'expired');
          } else if (!showActive && !showClosed) {
            filteredListings = [];
          }
          if (searchTerm.trim()) {
            filteredListings = filteredListings.filter(l =>
              matchesSearch('listing', l, searchTerm)
            );
          }

          // Build merged item list with a sort key
          const DEMO_ADDR = '4521 willow creek dr';
          const PECAN_DEMO_ADDR = '456 pecan creek';
          const nullDate = (showClosed && !showActive) ? new Date('1900-01-01') : new Date('9999-12-31');
          const CLOSED_LISTING_STATUSES = ['sold', 'withdrawn', 'expired', 'canceled', 'cancelled'];

          const contractItems = visibleContracts.map(c => {
            const isItemClosed = isClosedContract(c);
            const sortDate = contractSortDate(c, showClosed && !showActive) || nullDate;
            return {
              _type: 'contract',
              _key: `c-${c.id}`,
              _isDemo: (c.property_address || '').toLowerCase().includes(DEMO_ADDR) ||
                (c.property_address || '').toLowerCase().includes(PECAN_DEMO_ADDR),
              _isPecanDemo: (c.property_address || '').toLowerCase().includes(PECAN_DEMO_ADDR),
              _sortDate: sortDate,
              _isClosed: isItemClosed,
              data: c,
            };
          });

          // Suppress listing cards that have a linked contract — the contract card
          // already represents the full transaction and the listing details are
          // embedded in the ContractDetail page.
          const listingItems = filteredListings
            .filter(l => !l.contract_id)
            .map(l => {
              const isItemClosed = CLOSED_LISTING_STATUSES.includes((l.status || '').toLowerCase());
              return {
                _type: 'listing',
                _key: `l-${l.id}`,
                _isDemo: (l.street_address || '').toLowerCase().includes(DEMO_ADDR) ||
                  (l.street_address || '').toLowerCase().includes(PECAN_DEMO_ADDR),
                _isPecanDemo: (l.street_address || '').toLowerCase().includes(PECAN_DEMO_ADDR),
                _sortDate: l.expiration_date ? parseLocalDate(l.expiration_date) : nullDate,
                _isClosed: isItemClosed,
                data: l,
              };
            });

          // Filter buyer agreements
          let filteredBuyerAgreements = showBuyerAgreements ? [...buyerAgreements].filter(ba => !ba.contract_id) : [];
          if (showActive && !showClosed) {
            filteredBuyerAgreements = filteredBuyerAgreements.filter(ba => ba.status === 'active');
          } else if (!showActive && showClosed) {
            filteredBuyerAgreements = filteredBuyerAgreements.filter(ba =>
              ba.status === 'cancelled' || ba.status === 'expired'
            );
          } else if (!showActive && !showClosed) {
            filteredBuyerAgreements = [];
          }
          if (searchTerm.trim()) {
            filteredBuyerAgreements = filteredBuyerAgreements.filter(ba =>
              matchesSearch('buyer_agreement', ba, searchTerm)
            );
          }

          const buyerAgreementItems = filteredBuyerAgreements.map(ba => {
            const isItemClosed = ba.status === 'cancelled' || ba.status === 'expired';
            return {
              _type: 'buyer_agreement',
              _key: `ba-${ba.id}`,
              _isDemo: false,
              _sortDate: ba.expiration_date ? parseLocalDate(ba.expiration_date) : nullDate,
              _isClosed: isItemClosed,
              data: ba,
            };
          });

          const merged = [...contractItems, ...listingItems, ...buyerAgreementItems].sort((a, b) => {
            if (a._isPecanDemo && !b._isPecanDemo) return -1;
            if (b._isPecanDemo && !a._isPecanDemo) return 1;
            if (a._isDemo && !b._isDemo) return -1;
            if (b._isDemo && !a._isDemo) return 1;
            return (showClosed && !showActive)
              ? (b._sortDate - a._sortDate)
              : (a._sortDate - b._sortDate);
          });

          let filteredReferrals = showReferrals ? [...referralAgreements] : [];
          if (showActive && !showClosed) {
            // Standalone referrals are active-only today.
          } else if (!showActive && showClosed) {
            filteredReferrals = [];
          } else if (!showActive && !showClosed) {
            filteredReferrals = [];
          }
          if (searchTerm.trim()) {
            filteredReferrals = filteredReferrals.filter((r) =>
              matchesSearch('referral', r, searchTerm)
            );
          }
          const referralItems = filteredReferrals.map((r) => ({
            _type: 'referral',
            _key: `ra-${r.id}`,
            _sortDate: r.created_at ? parseLocalDate(String(r.created_at).slice(0, 10)) : nullDate,
            data: r,
          }));

          const displayItems = [...merged, ...referralItems];
          const showGuestEmpty = userProfile?.is_transaction_guest === true && homepageTransactionCount === 0;

          if (displayItems.length === 0) {
            if (showFirstRunChecklist) {
              return (
                <div className="contract-grid">
                  <FirstRunChecklist
                    tmsConfig={tmsConfig}
                    onCreateNew={() => {
                      setCreateModalStartOnOther(false);
                      setShowCreateModal(true);
                    }}
                  />
                </div>
              );
            }
            if (showGuestEmpty) {
              return (
                <div className="contract-grid">
                  <GuestHomeEmptyState onCreateNew={handleGuestUpgradePrompt} />
                </div>
              );
            }
            if (homepageTransactionCount > 0) {
              return (
                <div className="contract-grid">
                  <div className="no-contracts">
                    <div className="no-contracts-container" style={{ textAlign: 'center', marginTop: '50px' }}>
                      <h3>No files match your filters</h3>
                      <p>Try adjusting the Active/Closed or type filters above.</p>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="contract-grid">
                <div className="no-contracts">
                  <div className="no-contracts-container" style={{ textAlign: 'center', marginTop: '50px' }}>
                    <h3>Welcome to KeyFlo!</h3>
                    <p>You don&apos;t have any active contracts yet.</p>
                    <p>
                      To get started, forward your executed contract or listing agreement PDF to{' '}
                      <a href="mailto:tc@keyflo.ai">tc@keyflo.ai</a>.
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div className="contract-grid">
              {displayItems.map(item =>
                item._type === 'contract' ? (
                  <ContractCard
                    key={item._key}
                    contract={item.data}
                    onClick={() => handleContractClick(item.data.id)}
                    hideBadges={item._isClosed}
                    isClosed={item._isClosed}
                    isSuperAdmin={userProfile?.is_super_admin === true}
                  />
                ) : item._type === 'listing' ? (
                  <ListingCard key={item._key} listing={item.data} isClosed={item._isClosed} isSuperAdmin={userProfile?.is_super_admin === true} />
                ) : item._type === 'referral' ? (
                  <ReferralAgreementCard key={item._key} referral={item.data} isSuperAdmin={userProfile?.is_super_admin === true} />
                ) : (
                  <BuyerAgreementCard key={item._key} agreement={item.data} isClosed={item._isClosed} isSuperAdmin={userProfile?.is_super_admin === true} />
                )
              )}
            </div>
          );
        })()}
      </main>

      <CreateFormModal
        isOpen={showCreateModal}
        userEmail={user?.email}
        getAccessTokenSilently={getAccessTokenSilently}
        startOnOther={createModalStartOnOther}
        upgradeRequired={
          (accessReady || viewerProfile)
          && formGenerationPlanBlocked
          && !canGenerateForms
        }
        currentPlanName={currentPlanName}
        upgradePlanName={upgradePlanName}
        onClose={() => {
          setShowCreateModal(false);
          setCreateModalStartOnOther(false);
        }}
        onSelectForm={(formType, card) => {
          const next = startDashboardForm(formType);
          dashboardCreatedRef.current = next.createdResult;
          setShowCreateModal(next.showCreateModal);
          setDashboardFormType(next.dashboardFormType);
          setDashboardFormCard(card || null);
        }}
      />
      {(() => {
        const spec = wizardSpecForFormType(dashboardFormType, {
          createsEntity: dashboardFormCard?.createsEntity,
          catalog: dashboardFormCard
            ? [{ group: 'picked', forms: [dashboardFormCard] }]
            : undefined,
        });
        if (!spec) return null;
        const Wizard = spec.Wizard;
        const portalOnSubmit = buildPortalCreateSubmit(spec, {
          baseUrl: BUYER_AGREEMENTS_URL,
          userEmail: user?.email,
          post: (url, body) => axios.post(url, body),
        });
        return (
          <Wizard
            {...spec.buildDashboardProps({
              userEmail: user?.email,
              portalOnSubmit,
              wizardSteps: dashboardFormCard?.wizardSteps || [],
              optionalWizardFields: dashboardFormCard?.optionalWizardFields || [],
              stillNeededLabels: dashboardFormCard?.stillNeededLabels || {},
              allowEarlySubmitAfter: dashboardFormCard?.allowEarlySubmitAfter || '',
              onSuccess: (result) => {
                dashboardCreatedRef.current = result;
              },
              onClose: handleDashboardWizardClose,
              onBackToCatalog: () => {
                const next = returnToCatalogPicker(dashboardFormType);
                dashboardCreatedRef.current = next.createdResult;
                setShowCreateModal(next.showCreateModal);
                setCreateModalStartOnOther(next.startOnOther);
                setDashboardFormType(next.dashboardFormType);
                setDashboardFormCard(null);
              },
            })}
          />
        );
      })()}
      <GuestUpgradeModal
        isOpen={showGuestUpgradeModal}
        onClose={() => setShowGuestUpgradeModal(false)}
        onUpgrade={handleGuestUpgrade}
        freeTrialDeals={guestUpgradeOffer.freeTrialDeals}
        planNames={guestUpgradeOffer.planNames}
        loading={guestUpgradeOffer.loading}
      />
    </div>
  );
};

export default ContractsList;
